import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const POLL_MAX = 8;
const POLL_DELAY = 2000;

export default function AdvertiserCheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(() => ({
    status: sessionId ? "polling" : "error",
    campaign: null,
    attempts: 0,
  }));

  useEffect(() => {
    if (!sessionId) return;
    const ctrl = { cancelled: false };
    let attempts = 0;

    const poll = async () => {
      try {
        const { data: payment } = await apiClient.get(`/payments/status/${sessionId}`);
        const adId = payment?.metadata?.ad_campaign_id;
        if (adId) {
          const { data } = await apiClient.post(`/advertiser/campaigns/${adId}/sync-payment`);
          if (ctrl.cancelled) return;
          if (data.paid) {
            setState({ status: "success", campaign: data.campaign });
            return;
          }
        }
      } catch (e) {
        // continue polling
      }
      attempts += 1;
      if (ctrl.cancelled) return;
      if (attempts >= POLL_MAX) {
        setState({ status: "timeout", campaign: null });
        return;
      }
      setState(s => ({ ...s, attempts }));
      setTimeout(poll, POLL_DELAY);
    };
    poll();
    return () => { ctrl.cancelled = true; };
  }, [sessionId]);

  if (state.status === "polling") {
    return (
      <div className="max-w-xl mx-auto px-5 py-20 text-center" data-testid="checkout-polling">
        <Loader2 className="w-12 h-12 mx-auto text-[#c9a55c] animate-spin mb-4" />
        <h1 className="font-display text-3xl mb-2">{lang === "ar" ? "نتأكد من الدفع..." : "Confirming your payment..."}</h1>
        <p className="text-[#6b6b6b]">{lang === "ar" ? "لحظات من فضلك. لا تغلق هذه الصفحة." : "Hang on. Don't close this page."}</p>
      </div>
    );
  }

  if (state.status === "error" || state.status === "timeout") {
    return (
      <div className="max-w-xl mx-auto px-5 py-20 text-center" data-testid="checkout-error">
        <XCircle className="w-12 h-12 mx-auto text-amber-600 mb-4" />
        <h1 className="font-display text-3xl mb-2">{lang === "ar" ? "تعذّر تأكيد الدفع" : "Could not confirm payment"}</h1>
        <p className="text-[#6b6b6b] mb-6">
          {lang === "ar"
            ? "إذا كان الدفع تم بنجاح، ستظهر الحملة في لوحتك خلال دقائق. تواصل مع الدعم إذا استمرت المشكلة."
            : "If your payment succeeded, the campaign will appear in your dashboard shortly. Contact support if the issue persists."}
        </p>
        <button onClick={() => navigate("/advertiser/dashboard")} className="btn-primary mx-auto">
          {lang === "ar" ? "اذهب إلى لوحتي" : "Go to dashboard"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const c = state.campaign;
  return (
    <div className="max-w-xl mx-auto px-5 py-20 text-center" data-testid="checkout-success">
      <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
        <CheckCircle2 className="w-12 h-12 text-green-600" />
      </div>
      <div className="overline overline-gold mb-3">{lang === "ar" ? "تم بنجاح" : "Success"}</div>
      <h1 className="font-display text-4xl mb-3">{lang === "ar" ? "حملتك أصبحت نشطة!" : "Your campaign is live!"}</h1>
      <p className="text-[#6b6b6b] mb-8">
        {lang === "ar"
          ? `تم استلام الدفع بنجاح. سيظهر إعلانك "${c?.title}" خلال دقائق على ${c?.placement}.`
          : `Payment received. Your ad "${c?.title}" will start showing on ${c?.placement} shortly.`}
      </p>

      <div className="surface rounded-2xl p-5 text-start mb-8">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-[#8a8a8a] text-xs">{lang === "ar" ? "المُعلِن" : "Advertiser"}</div><div className="font-medium">{c?.advertiser_name}</div></div>
          <div><div className="text-[#8a8a8a] text-xs">{lang === "ar" ? "المبلغ" : "Amount"}</div><div className="font-medium">{formatSAR(c?.price_sar || 0)} SAR</div></div>
          <div><div className="text-[#8a8a8a] text-xs">{lang === "ar" ? "البداية" : "Starts"}</div><div className="font-medium">{c?.start_date}</div></div>
          <div><div className="text-[#8a8a8a] text-xs">{lang === "ar" ? "النهاية" : "Ends"}</div><div className="font-medium">{c?.end_date}</div></div>
        </div>
      </div>

      <div className="flex justify-center gap-3 flex-wrap">
        <button onClick={() => navigate("/advertiser/dashboard")} className="btn-primary" data-testid="goto-dashboard">
          {lang === "ar" ? "إلى لوحتي" : "My dashboard"} <ArrowRight className="w-4 h-4" />
        </button>
        <button onClick={() => navigate("/")} className="btn-ghost">{lang === "ar" ? "الصفحة الرئيسية" : "Homepage"}</button>
      </div>
    </div>
  );
}
